import classNames from 'classnames'
import { useTranslation } from 'next-i18next'

import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import usePromoActive, { usePromoGeoOffer } from '~/shared/hooks/usePromoActive'

export type PromoBadgeTheme = 'lime' | 'purple'

interface PromoBadgeStyle {
  rotate: string
  card: string
  headline: string
  percent: string
  off: string
  pill: string
  pillText: string
}

const THEMES: Record<PromoBadgeTheme, PromoBadgeStyle> = {
  lime: {
    rotate: 'rotate-16',
    card: 'bg-green-lime',
    headline: 'text-black-rich',
    percent: 'text-black-rich',
    off: 'text-black-rich',
    pill: 'bg-blue-pastel',
    pillText: 'text-black-rich',
  },
  purple: {
    rotate: 'rotate-9',
    card: 'bg-gradient-v4-cta-band',
    headline: 'text-green-lime',
    percent: 'text-white',
    off: 'text-white',
    pill: 'bg-lime-200',
    pillText: 'text-black-rich',
  },
}

interface PromoBadgeProps {
  theme: PromoBadgeTheme
  namespace: string
  keyPrefix: string
  className?: string
  /** Optional Tailwind rotate class to override the theme's default tilt (e.g. `rotate-20`). */
  rotate?: string
  showFootnoteMarker?: boolean
  /** The partner page it sits on, so a partner that keeps the promo still shows it. */
  partnerDetails?: PartnerDetailsType
}

export default function PromoBadge({
  theme,
  namespace,
  keyPrefix,
  className = '',
  rotate,
  showFootnoteMarker,
  partnerDetails,
}: PromoBadgeProps): React.ReactElement | null {
  const { t } = useTranslation(namespace, { keyPrefix })
  const promoActive = usePromoActive(partnerDetails)
  const geoOffer = usePromoGeoOffer()
  const style = THEMES[theme]

  // Hidden unless the visitor is eligible for the promo (defaults to hidden while geo resolves).
  if (!promoActive) {
    return null
  }

  return (
    <div className={classNames(rotate || style.rotate, className)}>
      <div className="relative inline-block select-none">
        <div
          aria-hidden
          className="absolute -inset-2 rounded-20 border-2 border-dashed border-white bg-purple/20 shadow"
        />

        <div className={classNames('relative rounded-20 py-4 pl-5 pr-11 shadow-cta-drop', style.card)}>
          <div className="flex items-center gap-3">
            <p
              className={classNames(
                'w-fit shrink-0 whitespace-pre-line text-right text-sm font-bold leading-4 text-shadow-md md:text-md',
                style.headline,
              )}
            >
              {t('headline')}
            </p>
            <p
              className={classNames(
                'text-h1 font-extrabold leading-none text-shadow-md md:text-v4-display',
                style.percent,
              )}
            >
              {typeof geoOffer?.percent === 'number' ? `${geoOffer.percent}%` : t('percent')}
              {showFootnoteMarker && <span className="align-super text-7 leading-none md:text-9">*</span>}
            </p>
          </div>
          <span className="absolute inset-y-0 right-4 flex w-6 items-center justify-center">
            <span
              className={classNames(
                '-rotate-90 whitespace-nowrap text-md font-bold leading-none text-shadow-md md:text-5',
                style.off,
              )}
            >
              {t('off')}
            </span>
          </span>
        </div>

        <p
          className={classNames(
            'absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-5 py-1.5 text-sm font-bold shadow-cta-drop md:text-md',
            style.pill,
            style.pillText,
          )}
        >
          {geoOffer?.price ? t('price', { price: geoOffer.price }) : t('price')}
        </p>
      </div>
    </div>
  )
}
